import {
  useMutation,
  useQuery,
  useQueryClient,
} from '@tanstack/react-query';
import * as api from '../lib/repository';
import type { ChecklistItem, ChecklistKind, Receipt, ReceiptCategory } from '../types';

export type { ReceiptEditableFields } from '../lib/repository';

export const qk = {
  trips: ['tb', 'trips'] as const,
  trip: (id: string) => ['tb', 'trip', id] as const,
  receipts: (tripId: string) => ['tb', 'receipts', tripId] as const,
  routes: (tripId: string) => ['tb', 'routes', tripId] as const,
  templates: (kind?: ChecklistKind) => ['tb', 'templates', kind ?? 'all'] as const,
  instances: (tripId: string, kind?: ChecklistKind) =>
    ['tb', 'instances', tripId, kind ?? 'all'] as const,
  items: (instanceId: string) => ['tb', 'items', instanceId] as const,
  summary: (tripId: string) => ['tb', 'summary', tripId] as const,
};

export function useTrips() {
  return useQuery({ queryKey: qk.trips, queryFn: api.listTrips });
}

export function useTrip(id: string | undefined) {
  return useQuery({
    queryKey: id ? qk.trip(id) : ['tb', 'trip', 'none'],
    queryFn: () => api.getTrip(id!),
    enabled: Boolean(id),
  });
}

export function useReceipts(tripId: string | undefined) {
  return useQuery({
    queryKey: tripId ? qk.receipts(tripId) : ['tb', 'receipts', 'none'],
    queryFn: () => api.listReceipts(tripId!),
    enabled: Boolean(tripId),
  });
}

export function useTemplates(kind?: ChecklistKind) {
  return useQuery({
    queryKey: qk.templates(kind),
    queryFn: () => api.listTemplates(kind),
  });
}

export function useInstances(tripId: string | undefined, kind?: ChecklistKind) {
  return useQuery({
    queryKey: tripId ? qk.instances(tripId, kind) : ['tb', 'instances', 'none'],
    queryFn: () => api.listInstancesForTrip(tripId!, kind),
    enabled: Boolean(tripId),
  });
}

export function useChecklistItems(instanceId: string | undefined) {
  return useQuery({
    queryKey: instanceId ? qk.items(instanceId) : ['tb', 'items', 'none'],
    queryFn: () => api.listItemsForInstance(instanceId!),
    enabled: Boolean(instanceId),
  });
}

export function useSpendSummary(tripId: string | undefined) {
  return useQuery({
    queryKey: tripId ? qk.summary(tripId) : ['tb', 'summary', 'none'],
    queryFn: () => api.spendSummary(tripId!),
    enabled: Boolean(tripId),
  });
}

export function useRoutes(tripId: string | undefined) {
  return useQuery({
    queryKey: tripId ? qk.routes(tripId) : ['tb', 'routes', 'none'],
    queryFn: () => api.listRoutes(tripId!),
    enabled: Boolean(tripId),
  });
}

export function useAddRoute(tripId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: Parameters<typeof api.addRoute>[1]) => api.addRoute(tripId, input),
    onSuccess: () => qc.invalidateQueries({ queryKey: qk.routes(tripId) }),
  });
}

export function useRemoveRoute(tripId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: api.removeRoute,
    onSuccess: () => qc.invalidateQueries({ queryKey: qk.routes(tripId) }),
  });
}

export function useUpdateRoute(tripId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({
      routeId,
      patch,
    }: {
      routeId: string;
      patch: Parameters<typeof api.updateRoute>[1];
    }) => api.updateRoute(routeId, patch),
    onSuccess: () => qc.invalidateQueries({ queryKey: qk.routes(tripId) }),
  });
}

export function useUpdateReceiptCategory(tripId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({
      receiptId,
      category,
    }: {
      receiptId: string;
      category: ReceiptCategory;
    }) => api.updateReceiptCategory(receiptId, category),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: qk.receipts(tripId) });
      qc.invalidateQueries({ queryKey: qk.summary(tripId) });
    },
  });
}

export function useUpdateReceipt(tripId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({
      receiptId,
      patch,
    }: {
      receiptId: string;
      patch: api.ReceiptEditableFields;
    }) => api.updateReceipt(receiptId, patch),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: qk.receipts(tripId) });
      qc.invalidateQueries({ queryKey: qk.summary(tripId) });
    },
  });
}

export function useCreateTrip() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: api.createTrip,
    onSuccess: (trip) => {
      qc.invalidateQueries({ queryKey: qk.trips });
      qc.invalidateQueries({ queryKey: ['tb', 'instances', trip.id] });
    },
  });
}

export function useDeleteTrip() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: api.deleteTrip,
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['tb'] });
    },
  });
}

export function useAddReceipt() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({
      tripId,
      file,
      onProgress,
    }: {
      tripId: string;
      file: File;
      onProgress?: (msg: string) => void;
    }) => api.addReceiptFromFile(tripId, file, onProgress),
    onSuccess: (_r, { tripId }) => {
      qc.invalidateQueries({ queryKey: qk.receipts(tripId) });
      qc.invalidateQueries({ queryKey: qk.summary(tripId) });
      qc.invalidateQueries({ queryKey: qk.routes(tripId) });
    },
  });
}

export function useResyncReceiptRoutes(tripId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({
      receiptId,
      onProgress,
    }: {
      receiptId: string;
      onProgress?: (msg: string) => void;
    }) => api.resyncRoutesFromStoredReceipt(tripId, receiptId, onProgress),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: qk.receipts(tripId) });
      qc.invalidateQueries({ queryKey: qk.routes(tripId) });
    },
  });
}

export function useRemoveReceipt(tripId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: api.removeReceipt,
    onMutate: async (receiptId) => {
      await qc.cancelQueries({ queryKey: qk.receipts(tripId) });
      const previous = qc.getQueryData<Receipt[]>(qk.receipts(tripId));
      qc.setQueryData<Receipt[]>(qk.receipts(tripId), (old) =>
        old?.filter((r) => r.id !== receiptId) ?? []
      );
      return { previous };
    },
    onError: (_e, _id, ctx) => {
      if (ctx?.previous) {
        qc.setQueryData(qk.receipts(tripId), ctx.previous);
      }
    },
    onSettled: () => {
      qc.invalidateQueries({ queryKey: qk.receipts(tripId) });
      qc.invalidateQueries({ queryKey: qk.summary(tripId) });
      qc.invalidateQueries({ queryKey: qk.routes(tripId) });
    },
  });
}

export function useCreateTemplate() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: api.createTemplate,
    onSuccess: () => qc.invalidateQueries({ queryKey: ['tb', 'templates'] }),
  });
}

export function useDeleteTemplate() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: api.deleteTemplate,
    onSuccess: () => qc.invalidateQueries({ queryKey: ['tb', 'templates'] }),
  });
}

export function useAddChecklistFromTemplate() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({
      tripId,
      templateId,
    }: {
      tripId: string;
      templateId: string;
    }) => api.addChecklistFromTemplate(tripId, templateId),
    onSuccess: (data, { tripId }) => {
      qc.invalidateQueries({ queryKey: ['tb', 'instances', tripId] });
      qc.invalidateQueries({ queryKey: qk.items(data.instance.id) });
    },
  });
}

export function useTogglePacked(instanceId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (itemId: string) => api.toggleItemPacked(itemId),
    onMutate: async (itemId) => {
      await qc.cancelQueries({ queryKey: qk.items(instanceId) });
      const previous = qc.getQueryData<ChecklistItem[]>(qk.items(instanceId));
      qc.setQueryData<ChecklistItem[]>(qk.items(instanceId), (old) => {
        if (!old) return old;
        const toggled = old.map((i) =>
          i.id === itemId ? { ...i, is_packed: !i.is_packed } : i
        );
        const u = toggled.filter((i) => !i.is_packed).sort((a, b) => a.sort_order - b.sort_order);
        const p = toggled.filter((i) => i.is_packed).sort((a, b) => a.sort_order - b.sort_order);
        let o = 0;
        return [...u, ...p].map((it) => ({ ...it, sort_order: o++ }));
      });
      return { previous };
    },
    onError: (_e, _id, ctx) => {
      if (ctx?.previous) {
        qc.setQueryData(qk.items(instanceId), ctx.previous);
      }
    },
    onSettled: () => {
      qc.invalidateQueries({ queryKey: qk.items(instanceId) });
    },
  });
}

export function useAddChecklistItem(instanceId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (label: string) => api.addChecklistItem(instanceId, label),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: qk.items(instanceId) });
    },
  });
}

export function useRemoveChecklistItem(instanceId: string, tripId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (itemId: string) => api.removeChecklistItem(itemId),
    onMutate: async (itemId) => {
      await qc.cancelQueries({ queryKey: qk.items(instanceId) });
      const previous = qc.getQueryData<ChecklistItem[]>(qk.items(instanceId));
      qc.setQueryData<ChecklistItem[]>(qk.items(instanceId), (old) =>
        old?.filter((i) => i.id !== itemId) ?? []
      );
      return { previous };
    },
    onError: (_e, _id, ctx) => {
      if (ctx?.previous) {
        qc.setQueryData(qk.items(instanceId), ctx.previous);
      }
    },
    onSettled: () => {
      qc.invalidateQueries({ queryKey: qk.items(instanceId) });
      qc.invalidateQueries({ queryKey: ['tb', 'instances', tripId] });
    },
  });
}

export function useReorderItems(instanceId: string, tripId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (orderedIds: string[]) =>
      api.reorderChecklistItems(instanceId, orderedIds),
    onMutate: async (orderedUnpackedIds) => {
      await qc.cancelQueries({ queryKey: qk.items(instanceId) });
      const previous = qc.getQueryData<ChecklistItem[]>(qk.items(instanceId));
      const map = new Map(previous?.map((i) => [i.id, i]));
      const packed = previous?.filter((i) => i.is_packed) ?? [];
      const reorderedUnpacked = orderedUnpackedIds
        .map((id, idx) => {
          const row = map.get(id);
          return row ? { ...row, sort_order: idx, is_packed: false } : null;
        })
        .filter(Boolean) as ChecklistItem[];
      const next: ChecklistItem[] = [
        ...reorderedUnpacked,
        ...packed.map((it, idx) => ({
          ...it,
          sort_order: reorderedUnpacked.length + idx,
        })),
      ];
      qc.setQueryData(qk.items(instanceId), next);
      return { previous };
    },
    onError: (_e, _ids, ctx) => {
      if (ctx?.previous) {
        qc.setQueryData(qk.items(instanceId), ctx.previous);
      }
    },
    onSettled: () => {
      qc.invalidateQueries({ queryKey: qk.items(instanceId) });
      qc.invalidateQueries({ queryKey: ['tb', 'instances', tripId] });
    },
  });
}

export function useClearLocalData() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: api.clearAllLocalData,
    onSuccess: () => qc.invalidateQueries(),
  });
}
